import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { body } from '../../styles/type';
import { fontWeights } from '../../theme/type';
import { colors } from '../../theme/colors';

import { rem } from '../../utilities/style';

import DropdownLink from './DropdownLink';
import Link from '../Link';
import TextRenderer from '../TextRenderer';

const DropdownItems = styled.ul`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  z-index: 2;
  padding-top: ${rem(8)};
`;

const DropdownItem = styled.li`
  padding: ${rem(6)} 0;

  ${({ cta }) => cta && `
    margin-top: auto;
    padding: ${rem(24)} 0 0;
  `}
`;

const DropdownTitle = styled.p`
  font-weight: ${fontWeights.medium};
`;

const DropdownDescription = styled.p`
  padding: ${rem(2)} 0;
  font-size: ${rem(12)};
  text-decoration: none !important;
  display: inline-block;
  color: ${colors.gray['600']} !important;
`;

const DropdownWrapper = styled.div`

`;

const NavPill = styled.span`
  ${body.small}
  font-weight: ${fontWeights.semiBold};
  background-color: ${colors.blue['500']};
  color: ${colors.white};
  width: fit-content;
  border-radius: ${rem(100)};
  padding: ${rem(4)} ${rem(12)};
  margin-bottom: ${rem(0)};
  margin-right: 5px;
  span {
    display: inline-block;
  }

  ${({ theme }) => !theme.pageOverrides?.includes('Dark Mode') && `
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.24);
  `}
`;

const NavPillContainer = styled.div`
  display: flex;
  align-items: center;
`;

const DropdownMenu = ({ items, linkProps }) => {
  if (!items) return null;

  return (
    <DropdownItems>
      {items && items.map((item, index) => {
        const dropdownLinkProps = linkProps && linkProps[index] !== undefined
          ? linkProps[index] : {};

        return (
          <DropdownItem
            key={item.id || index}
            cta={item.type === 'Primary' || item.type === 'Secondary'}
          >
            <DropdownLink
              component={Link}
              to={item.url}
              targetBlank={item.targetBlank}
              onKeyDown={dropdownLinkProps.onKeyDown}
              tabIndex={dropdownLinkProps.tabIndex}
              role={dropdownLinkProps.role}
              ref={dropdownLinkProps.ref}
              button={item.type === 'Primary'}
              arrowLink={item.type === 'Secondary'}
            >
              {item.description ? (
                <DropdownWrapper>
                  {item.navPill ? (
                    <NavPillContainer>
                      <NavPill><span>{item.navPill}</span></NavPill>
                      <DropdownTitle><TextRenderer text={item.title} /></DropdownTitle>
                    </NavPillContainer>
                  ) : (
                    <DropdownTitle><TextRenderer text={item.title} /></DropdownTitle>
                  )}
                  {(item.description
                    && (<DropdownDescription>{item.description}</DropdownDescription>))}
                </DropdownWrapper>
              ) : (
                <DropdownWrapper>
                  {item.navPill ? (
                    <NavPillContainer>
                      <NavPill><span>{item.navPill}</span></NavPill>
                      <TextRenderer text={item.title} />
                    </NavPillContainer>
                  ) : (
                    <TextRenderer text={item.title} />
                  )}
                </DropdownWrapper>
              )}
            </DropdownLink>
          </DropdownItem>
        );
      })}
    </DropdownItems>
  );
};

DropdownMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string.isRequired,
      url: PropTypes.string,
      description: PropTypes.string,
      navPill: PropTypes.string,
      targetBlank: PropTypes.bool,
      backgroundColor: PropTypes.string
    })
  ),
  linkProps: PropTypes.arrayOf(
    PropTypes.shape({
      onKeyDown: PropTypes.func,
      tabIndex: PropTypes.number,
      role: PropTypes.string,
      ref: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
    })
  )
};

export default DropdownMenu;
